// src/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/exportRentersTenantsDetailed.js

import ExcelJS from "exceljs";
import {
  getDetailedRentersDataStats,
  filterDetailedRentersData,
  getDetailedRentersTenantData
} from "./detailedRentersTenantData.js";
import { getRentersTenantsDashboardData } from "./rentersTenantsDashboardData.js";

/**
 * Export Renters/Tenants Detailed 15-Minute Interval Data to Excel
 */
export async function exportRentersTenantsDetailedToExcel(filters = {}) {
  try {
    const stats = getDetailedRentersDataStats();
    const currentDate = new Date().toLocaleDateString("en-GB");
    const dashboardData = getRentersTenantsDashboardData();

    // Get filtered detailed data
    let dailyData;

    if (filters.day || filters.month || filters.year) {
      dailyData = filterDetailedRentersData(filters);
    } else {
      const allMonthsData = getDetailedRentersTenantData();
      dailyData = [];
      allMonthsData.forEach((month) => {
        dailyData.push(...month.dailyData);
      });
    }

    // Get monthly summary data (for charts)
    let monthlyChartData = dashboardData.monthlyData;
    if (filters.year) {
      monthlyChartData = monthlyChartData.filter((m) => {
        const [year] = m.month.split("-");
        return parseInt(year) === parseInt(filters.year);
      });
    }
    if (filters.month) {
      monthlyChartData = monthlyChartData.filter(
        (m) => m.month === filters.month
      );
    }

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics - Renters/Tenants";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== SUMMARY SHEET ====================
    const summarySheet = workbook.addWorksheet("📊 Summary", {
      properties: { tabColor: { argb: "FF1E40AF" } }
    });
    createSummarySheet(summarySheet, stats, filters, dailyData);

    // ==================== DASHBOARD OVERVIEW ====================
    const overviewSheet = workbook.addWorksheet("📈 Dashboard Overview", {
      properties: { tabColor: { argb: "FF6366F1" } }
    });
    createDashboardOverviewSheet(overviewSheet, monthlyChartData);

    // ==================== MONTHLY CHART DATA ====================
    const monthlySheet = workbook.addWorksheet("📅 Monthly Data (Charts)", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });
    createMonthlyChartDataSheet(monthlySheet, monthlyChartData);

    // ==================== 15-MIN INTERVAL SHEETS ====================

    // Booking Utilization Details
    const utilizationSheet = workbook.addWorksheet(
      "📊 Booking Utilization (15-min)",
      {
        properties: { tabColor: { argb: "FF3B82F6" } }
      }
    );
    createBookingUtilizationTable(utilizationSheet, dailyData);

    // Energy per Booking Details
    const energySheet = workbook.addWorksheet(
      "⚡ Energy per Booking (15-min)",
      {
        properties: { tabColor: { argb: "FFF59E0B" } }
      }
    );
    createEnergyPerBookingTable(energySheet, dailyData);

    // Comfort Satisfaction Details
    const comfortSheet = workbook.addWorksheet(
      "😊 Comfort Satisfaction (15-min)",
      {
        properties: { tabColor: { argb: "FF10B981" } }
      }
    );
    createComfortSatisfactionTable(comfortSheet, dailyData);

    // Booking Duration Details
    const durationSheet = workbook.addWorksheet(
      "⏱️ Booking Duration (15-min)",
      {
        properties: { tabColor: { argb: "FF8B5CF6" } }
      }
    );
    createBookingDurationTable(durationSheet, dailyData);

    // CO2 Awareness Details
    const co2Sheet = workbook.addWorksheet("🌱 CO₂ Awareness (15-min)", {
      properties: { tabColor: { argb: "FF14B8A6" } }
    });
    createCO2AwarenessTable(co2Sheet, dailyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const filterSuffix = filters.day
      ? `Day-${filters.day}`
      : filters.month
      ? `Month-${filters.month}`
      : filters.year
      ? `Year-${filters.year}`
      : "Complete";
    link.download = `RentersTenants-Analytics-${filterSuffix}-${currentDate.replace(
      /\//g,
      "-"
    )}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting data to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== HELPER FUNCTIONS ====================

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } }
    };
  });
  sheet.getRow(1).height = 40;
}

function styleDataCell(cell, value, isEven, format = "text") {
  cell.value = value;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: isEven ? "FFFFFFFF" : "FFF9FAFB" }
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (format === "number") {
    cell.numFmt = "#,##0";
  } else if (format === "decimal") {
    cell.numFmt = "0.0";
  } else if (format === "currency") {
    cell.numFmt = "€#,##0.00";
  } else if (format === "percent") {
    cell.numFmt = '0.0"%"';
  } else if (format === "time") {
    cell.numFmt = "@";
  }
}

function createSummarySheet(sheet, stats, filters, dailyData) {
  sheet.columns = [{ width: 30 }, { width: 50 }];

  sheet.mergeCells("A1:B1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "RENTERS/TENANTS ANALYTICS - COMPLETE EXPORT";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E40AF" }
  };
  sheet.getRow(1).height = 35;

  const totalIntervals = dailyData.reduce(
    (sum, day) => sum + day.bookings.length,
    0
  );

  let row = 3;
  addDataRow(
    sheet,
    row++,
    "Export Type",
    "Complete Analytics (Dashboard + 15-min Intervals)"
  );
  addDataRow(sheet, row++, "Total Days Exported", dailyData.length);
  addDataRow(
    sheet,
    row++,
    "Total 15-min Intervals",
    totalIntervals.toLocaleString()
  );
  addDataRow(sheet, row++, "Interval Duration", "15 minutes");
  addDataRow(sheet, row++, "Operating Hours", "08:00 - 19:00");

  row++;
  addSectionHeader(sheet, row++, "Applied Filters");
  addDataRow(sheet, row++, "Year", filters.year || "All Available");
  addDataRow(sheet, row++, "Month", filters.month || "All Available");
  addDataRow(sheet, row++, "Day", filters.day || "All Available");

  row++;
  addSectionHeader(sheet, row++, "Sheet Structure");
  addDataRow(sheet, row++, "📊 Summary", "This overview sheet");
  addDataRow(
    sheet,
    row++,
    "📈 Dashboard Overview",
    "KPI cards shown on dashboard"
  );
  addDataRow(
    sheet,
    row++,
    "📅 Monthly Data",
    "Chart-level monthly aggregations"
  );
  addDataRow(
    sheet,
    row++,
    "📊 Booking Utilization (15-min)",
    "Detailed utilization intervals"
  );
  addDataRow(
    sheet,
    row++,
    "⚡ Energy per Booking (15-min)",
    "Detailed energy intervals"
  );
  addDataRow(
    sheet,
    row++,
    "😊 Comfort Satisfaction (15-min)",
    "Detailed comfort intervals"
  );
  addDataRow(
    sheet,
    row++,
    "⏱️ Booking Duration (15-min)",
    "Detailed duration intervals"
  );
  addDataRow(
    sheet,
    row++,
    "🌱 CO₂ Awareness (15-min)",
    "Detailed CO₂ intervals"
  );

  row++;
  addDataRow(
    sheet,
    row++,
    "Report Generated",
    new Date().toLocaleDateString("en-GB")
  );
}

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createDashboardOverviewSheet(sheet, monthlyData) {
  sheet.columns = [{ width: 25 }, { width: 20 }, { width: 15 }, { width: 30 }];

  sheet.mergeCells("A1:D1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "DASHBOARD OVERVIEW - KPI CARDS";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6366F1" }
  };
  sheet.getRow(1).height = 35;

  const latestMonth = monthlyData[monthlyData.length - 1];

  let row = 3;

  // Booking Utilization Section
  addSectionHeader2(sheet, row++, "📊 BOOKING UTILIZATION", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Utilization Rate",
    `${latestMonth.bookingUtilization.rate}%`,
    "Current",
    "Booked time vs. available time"
  );
  addOverviewRow(
    sheet,
    row++,
    "Booked Slots",
    `${latestMonth.bookingUtilization.bookedSlots}`,
    "Total",
    `Out of ${latestMonth.bookingUtilization.availableSlots} available`
  );
  addOverviewRow(
    sheet,
    row++,
    "Peak Days",
    latestMonth.bookingUtilization.peakDays.join(", "),
    "Schedule",
    "Highest utilization days"
  );

  row++;

  // Energy per Booking Section
  addSectionHeader2(sheet, row++, "⚡ ENERGY PER BOOKING", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Avg Energy per Booking",
    `${latestMonth.energyPerBooking.avgEnergy} kWh`,
    "Current",
    "Average energy consumed per booking"
  );
  addOverviewRow(
    sheet,
    row++,
    "Total Energy",
    `${latestMonth.energyPerBooking.totalEnergy} kWh`,
    "Monthly",
    "Total energy consumption"
  );
  addOverviewRow(
    sheet,
    row++,
    "Total Cost",
    `€${latestMonth.energyPerBooking.totalCost.toLocaleString()}`,
    "Monthly",
    "Total energy cost"
  );
  addOverviewRow(
    sheet,
    row++,
    "Benchmark Comparison",
    `${latestMonth.energyPerBooking.benchmarkComparison}%`,
    "Performance",
    "vs. industry benchmark"
  );

  row++;

  // Comfort Satisfaction Section
  addSectionHeader2(sheet, row++, "😊 COMFORT SATISFACTION", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Overall Score",
    `${latestMonth.comfortSatisfaction.overallScore}%`,
    "Current",
    "Overall comfort satisfaction index"
  );
  addOverviewRow(
    sheet,
    row++,
    "Energy Analytics",
    `${latestMonth.comfortSatisfaction.energyAnalytics}%`,
    "Score",
    "Energy efficiency perception"
  );
  addOverviewRow(
    sheet,
    row++,
    "Dynamic Occupancy",
    `${latestMonth.comfortSatisfaction.dynamicOccupancy}%`,
    "Score",
    "Real-time occupancy satisfaction"
  );

  row++;

  // Booking Duration Section
  addSectionHeader2(sheet, row++, "⏱️ BOOKING DURATION", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Avg Duration",
    `${latestMonth.bookingDuration.avgDuration} hrs`,
    "Current",
    "Average booking length"
  );
  addOverviewRow(
    sheet,
    row++,
    "Total Bookings",
    `${latestMonth.bookingDuration.totalBookings}`,
    "Monthly",
    "Total number of bookings"
  );
  addOverviewRow(
    sheet,
    row++,
    "Efficiency",
    `${latestMonth.bookingDuration.efficiency}%`,
    "Score",
    "Booking efficiency rating"
  );

  row++;

  // CO2 Awareness Section
  addSectionHeader2(sheet, row++, "🌱 CO₂ AWARENESS", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Overall Score",
    `${latestMonth.co2Awareness.overallScore}%`,
    "Current",
    "Overall CO₂ awareness index"
  );
  addOverviewRow(
    sheet,
    row++,
    "Energy Efficiency",
    `${latestMonth.co2Awareness.energyEfficiency}%`,
    "Score",
    "Energy efficiency rating"
  );
  addOverviewRow(
    sheet,
    row++,
    "Waste Diversion",
    `${latestMonth.co2Awareness.wasteDiversion}%`,
    "Score",
    "Waste recycling rate"
  );
  addOverviewRow(
    sheet,
    row++,
    "Water Conservation",
    `${latestMonth.co2Awareness.waterConservation}%`,
    "Score",
    "Water conservation rating"
  );
}

function addOverviewRow(sheet, rowNum, label, value, category, description) {
  const row = sheet.getRow(rowNum);

  row.getCell(1).value = label;
  row.getCell(1).font = { bold: true, size: 11 };
  row.getCell(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  row.getCell(2).value = value;
  row.getCell(2).font = { size: 11, bold: true, color: { argb: "FF2563EB" } };
  row.getCell(2).alignment = { horizontal: "center" };

  row.getCell(3).value = category;
  row.getCell(3).font = { size: 10, italic: true };
  row.getCell(3).alignment = { horizontal: "center" };

  row.getCell(4).value = description;
  row.getCell(4).font = { size: 10 };

  row.height = 22;

  row.eachCell((cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } }
    };
  });
}

function addSectionHeader2(sheet, rowNum, text, startCol, endCol) {
  sheet.mergeCells(`${startCol}${rowNum}:${endCol}${rowNum}`);
  const cell = sheet.getCell(`${startCol}${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createMonthlyChartDataSheet(sheet, monthlyData) {
  sheet.columns = [
    { width: 15 }, // Month
    { width: 12 }, // Utilization Rate
    { width: 12 }, // Avg Energy
    { width: 12 }, // Total Energy
    { width: 12 }, // Comfort Score
    { width: 12 }, // Avg Duration
    { width: 12 } // CO2 Score
  ];

  const headers = [
    "Month",
    "Utilization\nRate %",
    "Avg Energy\nper Booking\n(kWh)",
    "Total\nEnergy\n(kWh)",
    "Comfort\nScore %",
    "Avg\nDuration\n(hrs)",
    "CO₂\nAwareness %"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  monthlyData.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = rowNum % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 2),
      month.bookingUtilization.rate,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.energyPerBooking.avgEnergy,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.energyPerBooking.totalEnergy,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.comfortSatisfaction.overallScore,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.bookingDuration.avgDuration,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.co2Awareness.overallScore,
      isEven,
      "decimal"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

function createBookingUtilizationTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 15 }, // Utilization Rate
    { width: 12 } // Booked Slots
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Utilization\nRate %",
    "Booked\nSlots"
  ];
  createTableHeader(sheet, headers, "FF3B82F6");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.bookings.forEach((booking) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        booking.utilizationRate,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        booking.bookedSlots,
        isEven,
        "number"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createEnergyPerBookingTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 },
    { width: 12 },
    { width: 8 },
    { width: 12 },
    { width: 10 }
  ];

  const headers = ["Date", "Day of Week", "Time", "Energy\n(kWh)", "Cost\n(€)"];
  createTableHeader(sheet, headers, "FFF59E0B");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.energyIntervals.forEach((interval) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), interval.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        interval.energyKwh,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        interval.cost,
        isEven,
        "currency"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createComfortSatisfactionTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 },
    { width: 12 },
    { width: 8 },
    { width: 15 },
    { width: 15 }
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Comfort\nScore %",
    "Energy\nAnalytics %"
  ];
  createTableHeader(sheet, headers, "FF10B981");

  let rowNum = 2;
  dailyData.forEach((day) => {
    const comfortScore = day.dailySummary.comfortSatisfaction.overallScore;
    const energyAnalytics =
      day.dailySummary.comfortSatisfaction.energyAnalytics;

    day.bookings.forEach((booking) => {
      const isEven = rowNum % 2 === 0;
      const intervalScore =
        Math.round((comfortScore + (Math.random() - 0.5) * 3) * 10) / 10;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(sheet.getCell(rowNum, 4), intervalScore, isEven, "decimal");
      styleDataCell(
        sheet.getCell(rowNum, 5),
        energyAnalytics,
        isEven,
        "decimal"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createBookingDurationTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 },
    { width: 12 },
    { width: 8 },
    { width: 15 },
    { width: 12 }
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Avg Duration\n(hrs)",
    "Efficiency\n%"
  ];
  createTableHeader(sheet, headers, "FF8B5CF6");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.durationIntervals.forEach((interval) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), interval.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        interval.avgDuration,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        interval.efficiency,
        isEven,
        "decimal"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createCO2AwarenessTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 },
    { width: 12 },
    { width: 8 },
    { width: 15 },
    { width: 12 }
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Awareness\nScore %",
    "Emissions\n(kg CO₂)"
  ];
  createTableHeader(sheet, headers, "FF14B8A6");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.co2Intervals.forEach((interval) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), interval.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        interval.awarenessScore,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        interval.emissions,
        isEven,
        "decimal"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}
